import React, { useEffect, useState } from 'react';
import { endOfDay, parseISO, startOfDay } from 'date-fns';
import { Button, Form, Modal } from '@/components/ui';
import { monthsAgoPacific, todayPacific } from '../../../utils/wrapDate';

/** The report presets offered by Print All PMs and Print Repairs (EQ-18, EQ-26). */
export const REPORT_PRESET_MONTHS = [3, 6, 18, 24] as const;
const CUSTOM = 'custom';

interface EquipmentDateRangeModalProps {
  show: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  onHide: () => void;
  /** Receives local start-of-day `from` and end-of-day `to`. */
  onConfirm: (range: { from: Date; to: Date }) => Promise<void>;
  /**
   * Offer "Last n Months" presets (plus Custom, which shows From / To). Without it the dialog
   * shows only From / To, defaulting to the last two years.
   */
  presetMonths?: readonly number[];
  /** Preset selected when the dialog opens; defaults to the longest one. */
  defaultPresetMonths?: number;
  /** Extra content under the range (e.g. which PM types will print). */
  children?: React.ReactNode;
}

/** Date range prompt for unit reports. Defaults to the last two years, ending today (Pacific). */
const EquipmentDateRangeModal: React.FC<EquipmentDateRangeModalProps> = ({
  show,
  title,
  description,
  confirmLabel,
  onHide,
  onConfirm,
  presetMonths,
  defaultPresetMonths,
  children
}) => {
  const hasPresets = !!presetMonths && presetMonths.length > 0;
  const initialPreset = hasPresets
    ? String(defaultPresetMonths ?? Math.max(...(presetMonths as number[])))
    : CUSTOM;
  const [preset, setPreset] = useState<string>(initialPreset);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;
    setPreset(initialPreset);
    const months = initialPreset === CUSTOM ? 24 : Number(initialPreset);
    setFrom(monthsAgoPacific(months));
    setTo(todayPacific());
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const choosePreset = (value: string) => {
    setPreset(value);
    if (value !== CUSTOM) {
      setFrom(monthsAgoPacific(Number(value)));
      setTo(todayPacific());
    }
  };

  const invalid = !from || !to || from > to;
  const showDates = !hasPresets || preset === CUSTOM;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm({ from: startOfDay(parseISO(from)), to: endOfDay(parseISO(to)) });
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="sm">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {description && <p className="tw-text-sm tw-text-content-secondary">{description}</p>}
        {error && <p className="tw-mb-2 tw-text-sm tw-text-danger">{error}</p>}
        {hasPresets && (
          <>
            <Form.Label htmlFor="equipment-range-preset">Period</Form.Label>
            <Form.Select id="equipment-range-preset" value={preset} onChange={(e) => choosePreset(e.target.value)}>
              {(presetMonths as readonly number[]).map((months) => (
                <option key={months} value={String(months)}>
                  Last {months} Months
                </option>
              ))}
              <option value={CUSTOM}>Custom Range</option>
            </Form.Select>
            {!showDates && (
              <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-content-muted">
                {from} – {to}
              </p>
            )}
          </>
        )}
        {showDates && (
          <div className={hasPresets ? 'tw-mt-3' : undefined}>
            <Form.Label htmlFor="equipment-range-from">From</Form.Label>
            <Form.Control id="equipment-range-from" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            <Form.Label htmlFor="equipment-range-to" className="tw-mt-3">To</Form.Label>
            <Form.Control id="equipment-range-to" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            {from && to && from > to && (
              <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-danger">The start date must be on or before the end date.</p>
            )}
          </div>
        )}
        {children}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={busy}>Cancel</Button>
        <Button onClick={confirm} disabled={busy || invalid}>
          <i className="bi bi-printer tw-mr-1" aria-hidden="true" />
          {busy ? 'Preparing…' : confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EquipmentDateRangeModal;
